// v10/src/components/wb/CGEnvelopeChart.js
//
// Renderiza el envelope CG como SVG estático.
//
// Props:
//   - envelopePoints: Array<{ weight_lb, cg_min_in, cg_max_in }>
//   - flightPoint: { weight_lb, cg_in } - el punto del vuelo (o null)
//   - withinEnvelope: boolean
//   - darkMode: boolean

import React from 'react';
import { useTranslation } from 'react-i18next';

const CGEnvelopeChart = ({ envelopePoints, flightPoint, withinEnvelope, darkMode }) => {
  const { t } = useTranslation();

  const sub = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';

  if (!envelopePoints || envelopePoints.length < 2) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: sub,
          fontSize: 13,
          border: darkMode ? '1px dashed rgba(255,255,255,0.15)' : '1px dashed rgba(0,0,0,0.15)',
          borderRadius: 12,
        }}
      >
        {t('wb_no_envelope_data', { ns: 'admin' })}
      </div>
    );
  }

  // Normalizar puntos
  const normPoints = envelopePoints.map(p => ({
    weight_lb: Number(p.weight_lb),
    cg_min_in: Number(p.cg_min_in),
    cg_max_in: Number(p.cg_max_in),
  }));

  // Calcular bounds del gráfico con margen
  const allCgs = normPoints.flatMap(p => [p.cg_min_in, p.cg_max_in]);
  const allWeights = normPoints.map(p => p.weight_lb);

  let minCg = Math.min(...allCgs);
  let maxCg = Math.max(...allCgs);
  let minWeight = Math.min(...allWeights);
  let maxWeight = Math.max(...allWeights);

  // Si hay flightPoint, asegurarse de incluirlo en los bounds (queremos verlo aunque esté fuera)
  if (flightPoint && typeof flightPoint.cg_in === 'number' && typeof flightPoint.weight_lb === 'number') {
    minCg = Math.min(minCg, flightPoint.cg_in);
    maxCg = Math.max(maxCg, flightPoint.cg_in);
    minWeight = Math.min(minWeight, flightPoint.weight_lb);
    maxWeight = Math.max(maxWeight, flightPoint.weight_lb);
  }

  // Margen visual
  minCg -= 2;
  maxCg += 2;
  minWeight -= 100;
  maxWeight += 100;

  // SVG viewBox
  const width = 600;
  const height = 400;
  const margin = { top: 24, right: 24, bottom: 54, left: 70 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  // Funciones de escala
  const scaleX = (cg) =>
    margin.left + ((cg - minCg) / (maxCg - minCg)) * plotWidth;
  const scaleY = (weight) =>
    margin.top + plotHeight - ((weight - minWeight) / (maxWeight - minWeight)) * plotHeight;

  // Construir el polígono del envelope:
  //   primero los puntos forward (cg_min) ordenados por peso ascendente,
  //   luego los aft (cg_max) en reverso para cerrar el polígono.
  const sortedByWeight = [...normPoints].sort((a, b) => a.weight_lb - b.weight_lb);
  const polygonPoints = [
    ...sortedByWeight.map(p => `${scaleX(p.cg_min_in)},${scaleY(p.weight_lb)}`),
    ...sortedByWeight.slice().reverse().map(p => `${scaleX(p.cg_max_in)},${scaleY(p.weight_lb)}`),
  ].join(' ');

  // Colores
  const envelopeFillOk = darkMode ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.15)';
  const envelopeFillBad = darkMode ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.10)';
  const envelopeStroke = withinEnvelope === false ? '#ef4444' : '#10b981';
  const envelopeFill = withinEnvelope === false ? envelopeFillBad : envelopeFillOk;
  const pointColor = withinEnvelope === false ? '#ef4444' : '#10b981';
  const gridColor = darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';
  const axisColor = darkMode ? '#94a3b8' : '#64748b';
  const tickColor = darkMode ? 'rgba(255,255,255,0.55)' : '#475569';

  // Ticks del eje Y (peso) — cada 100 lb redondeados
  const weightStep = chooseStep(maxWeight - minWeight, [100, 200, 250, 500]);
  const weightTicks = [];
  const firstWeightTick = Math.ceil(minWeight / weightStep) * weightStep;
  for (let w = firstWeightTick; w <= maxWeight; w += weightStep) {
    weightTicks.push(w);
  }

  // Ticks del eje X (CG) — cada 1 in o 2 in
  const cgStep = chooseStep(maxCg - minCg, [1, 2, 5]);
  const cgTicks = [];
  const firstCgTick = Math.ceil(minCg / cgStep) * cgStep;
  for (let c = firstCgTick; c <= maxCg; c += cgStep) {
    cgTicks.push(Math.round(c * 100) / 100);
  }

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', maxWidth: 640, display: 'block' }}
        role="img"
        aria-label="CG Envelope"
      >
        {/* Grid horizontal (pesos) */}
        {weightTicks.map(w => (
          <line
            key={`gy-${w}`}
            x1={margin.left}
            x2={margin.left + plotWidth}
            y1={scaleY(w)}
            y2={scaleY(w)}
            stroke={gridColor}
            strokeWidth={1}
          />
        ))}

        {/* Grid vertical (CGs) */}
        {cgTicks.map(c => (
          <line
            key={`gx-${c}`}
            x1={scaleX(c)}
            x2={scaleX(c)}
            y1={margin.top}
            y2={margin.top + plotHeight}
            stroke={gridColor}
            strokeWidth={1}
          />
        ))}

        {/* Polígono del envelope */}
        <polygon
          points={polygonPoints}
          fill={envelopeFill}
          stroke={envelopeStroke}
          strokeWidth={2}
        />

        {/* Punto del vuelo (si existe) */}
        {flightPoint && typeof flightPoint.cg_in === 'number' && typeof flightPoint.weight_lb === 'number' && (
          <g>
            {/* Líneas guía */}
            <line
              x1={scaleX(flightPoint.cg_in)} y1={margin.top}
              x2={scaleX(flightPoint.cg_in)} y2={margin.top + plotHeight}
              stroke={pointColor} strokeWidth={1} strokeDasharray="4,4" opacity={0.5}
            />
            <line
              x1={margin.left} y1={scaleY(flightPoint.weight_lb)}
              x2={margin.left + plotWidth} y2={scaleY(flightPoint.weight_lb)}
              stroke={pointColor} strokeWidth={1} strokeDasharray="4,4" opacity={0.5}
            />
            {/* Punto */}
            <circle
              cx={scaleX(flightPoint.cg_in)}
              cy={scaleY(flightPoint.weight_lb)}
              r={8}
              fill={pointColor}
              stroke={darkMode ? '#0a0f1e' : 'white'}
              strokeWidth={2}
            />
          </g>
        )}

        {/* Ejes */}
        <line x1={margin.left} y1={margin.top + plotHeight}
              x2={margin.left + plotWidth} y2={margin.top + plotHeight}
              stroke={axisColor} strokeWidth={1} />
        <line x1={margin.left} y1={margin.top}
              x2={margin.left} y2={margin.top + plotHeight}
              stroke={axisColor} strokeWidth={1} />

        {/* Labels eje X */}
        {cgTicks.map(c => (
          <text
            key={`tx-${c}`}
            x={scaleX(c)}
            y={margin.top + plotHeight + 16}
            textAnchor="middle"
            fontSize={11}
            fill={tickColor}
          >
            {c}
          </text>
        ))}

        {/* Labels eje Y */}
        {weightTicks.map(w => (
          <text
            key={`ty-${w}`}
            x={margin.left - 8}
            y={scaleY(w) + 4}
            textAnchor="end"
            fontSize={11}
            fill={tickColor}
          >
            {w}
          </text>
        ))}

        {/* Títulos de ejes */}
        <text
          x={margin.left + plotWidth / 2}
          y={height - 12}
          textAnchor="middle"
          fill={axisColor}
          fontSize={13}
        >
          {t('wb_axis_cg', { ns: 'admin' })} ({t('wb_unit_inches', { ns: 'admin' })})
        </text>
        <text
          x={18}
          y={margin.top + plotHeight / 2}
          textAnchor="middle"
          fill={axisColor}
          fontSize={13}
          transform={`rotate(-90 18 ${margin.top + plotHeight / 2})`}
        >
          {t('wb_axis_weight', { ns: 'admin' })} ({t('wb_unit_lb', { ns: 'admin' })})
        </text>
      </svg>

      {/* Leyenda */}
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          fontSize: 12,
          color: sub,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 14,
              height: 10,
              background: envelopeFill,
              border: `1.5px solid ${envelopeStroke}`,
              borderRadius: 2,
              display: 'inline-block',
            }}
          />
          {t('wb_legend_envelope', { ns: 'admin' })}
        </span>
        {flightPoint && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: pointColor,
                border: `2px solid ${darkMode ? '#0a0f1e' : 'white'}`,
                display: 'inline-block',
              }}
            />
            {t('wb_legend_flight_point', { ns: 'admin' })}
          </span>
        )}
      </div>
    </div>
  );
};

// Elige el primer step de la lista que produzca entre 4 y 10 ticks aprox
function chooseStep(range, candidates) {
  for (const step of candidates) {
    const n = Math.ceil(range / step);
    if (n >= 4 && n <= 12) return step;
  }
  return candidates[candidates.length - 1];
}

export default CGEnvelopeChart;
